import { REQUEST_ACCESS_COOLDOWN_DAYS } from '@vacationtracker/shared/constants/request-access'

const STORAGE_KEY = 'vtAccessRequests'
const COOLDOWN_MS = REQUEST_ACCESS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000

type AccessRequestLog = Record<string, string>

/**
 * When each address last asked a company's administrators for access.
 *
 * Kept here rather than read back from the endpoint on purpose: it answers every request the
 * same way, including the ones it drops inside its cooldown, so that nobody can use it to
 * find out whether an address has an account. That makes this browser the only thing that
 * knows a request has already gone out, and without it the page reports a second request as
 * sent when the backend discarded it as a repeat.
 */
const readLog = (): AccessRequestLog => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return {}
    const parsed = JSON.parse(stored) as unknown
    return typeof parsed === 'object' && parsed !== null ? (parsed as AccessRequestLog) : {}
  } catch {
    return {}
  }
}

const keyFor = (email: string): string => email.trim().toLowerCase()

/** When this address may usefully ask again, or null if asking now would reach the admins. */
export const readAccessRequestRetryDate = (email: string, now = new Date()): Date | null => {
  const requestedAt = readLog()[keyFor(email)]
  if (!requestedAt) return null

  const retryOn = new Date(new Date(requestedAt).getTime() + COOLDOWN_MS)
  return retryOn.getTime() > now.getTime() ? retryOn : null
}

export const rememberAccessRequest = (email: string, now = new Date()): void => {
  try {
    const log = readLog()
    // Everything past its cooldown is noise the page will never read again.
    const fresh = Object.entries(log).filter(([, requestedAt]) => new Date(requestedAt).getTime() + COOLDOWN_MS > now.getTime())
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...Object.fromEntries(fresh),
        [keyFor(email)]: now.toISOString(),
      })
    )
  } catch {
    // A browser that refuses storage still gets the request sent; it just cannot be told
    // that a later one would be dropped.
  }
}

/**
 * Signing in means the request was answered. The endpoint stops counting it once the account
 * loses access again, so neither may this browser.
 */
export const forgetAccessRequest = (email: string): void => {
  try {
    const log = readLog()
    if (!(keyFor(email) in log)) return
    delete log[keyFor(email)]
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log))
  } catch {
    // Nothing remembered that cannot be read, so nothing to forget.
  }
}
